/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  UnauthenticatedError,
  UnauthorisedError
} from "@tivocorp/data-domain-lib";
import type { DomainToken } from "@tivocorp/data-domain-lib";

import { DeviceModel } from "../device";
import { Log } from "../logging/Log";
import type { ITokenRefreshStrategy } from "./ITokenRefreshStrategy";
import { TokenStore, type TStoredToken } from "./TokenStore";
import { ACCOUNT_CONTEXT_ID, TOKEN_EXPIRY_SKEW_MS } from "./tokenConstants";
import { bumpTokenGeneration } from "./tokenGeneration";

/** One context's token as held in memory. */
interface TTokenEntry {
  token: string;
  domain: string;
  /** Expiry in epoch **milliseconds** — seconds are only the wire format. */
  expirationMs: number;
}

/**
 * A resolved credential: the token, the context it was minted for, and its
 * expiry. Returned as one object so a caller cannot pair a token with a
 * context or an expiry read at a different moment.
 */
export interface TAuthContext {
  accessToken: string;
  profileId: string | null;
  domain: string;
  /** Expiry as Unix epoch seconds, matching the domain-token wire format. */
  expiration: number;
}

/**
 * The single source of a valid access token.
 *
 * Nothing else in the app may read a persisted token. Every caller that needs
 * one calls {@link getValidAccessToken} (or {@link getAuthContext} when it also
 * needs the context the token belongs to) and gets back a token guaranteed to
 * be outside the expiry skew window, refreshing transparently if it is not.
 *
 * Resolution order, per context:
 *   1. in memory and still fresh
 *   2. in IndexedDB and still fresh — hydrated into memory
 *   3. refreshed through the registered {@link ITokenRefreshStrategy}, then
 *      persisted (records never expire in storage — freshness is decided
 *      here, from the token's `expiration` and the 5-minute skew window)
 *
 * Contexts
 * --------
 * A token is either account-level (`profileId === null`) or scoped to one
 * profile. The account context is the default and stays active until
 * {@link setActiveContext} is called with a profile ID — which
 * `activateProfile` does once the user picks a profile, and which reverts to
 * `null` when that profile is deleted. Calls that need a specific context pass
 * `profileId` explicitly; nothing infers a profile context on its own.
 *
 * Every method is safe to call before sign-in: {@link restore} runs lazily on
 * first use, so there is no window in which callers must fall back to reading
 * storage themselves.
 */
export class TokenManager {
  private static TAG: string = "TokenManager";

  /** Fresh tokens by context key. */
  private static mEntries: Map<string, TTokenEntry> = new Map<
    string,
    TTokenEntry
  >();

  /**
   * In-flight refreshes by context key. Keyed rather than global so an account
   * refresh and a profile refresh cannot join each other's promise and receive
   * a token minted for the wrong context.
   */
  private static mInFlight: Map<string, Promise<string>> = new Map<
    string,
    Promise<string>
  >();

  private static mStrategy: ITokenRefreshStrategy | null = null;
  private static mActiveProfileId: string | null = null;
  private static mIsRestored: boolean = false;
  private static mRestorePromise: Promise<void> | null = null;

  /**
   * Registers the refresh strategy. Called once from the composition root.
   * Without one, TokenManager can still serve tokens from memory and storage
   * but cannot mint new ones.
   */
  public static setRefreshStrategy(strategy: ITokenRefreshStrategy): void {
    TokenManager.mStrategy = strategy;
  }

  /** Context key for a profile ID. `null`/empty is the account context. */
  private static contextKey(profileId: string | null): string {
    return profileId !== null && profileId.length > 0
      ? profileId
      : ACCOUNT_CONTEXT_ID;
  }

  /** True when the token is missing or inside the expiry skew window. */
  private static isFresh(expirationMs: number): boolean {
    return expirationMs - Date.now() > TOKEN_EXPIRY_SKEW_MS;
  }

  /**
   * Hydrates in-memory state from IndexedDB. Idempotent, single-flight, and
   * called lazily by every accessor, so callers never need to sequence it.
   *
   * Restores the **account** context as the active one. The profile gate has
   * to call the profile-management service before it knows which persisted
   * profile is still valid, so boot is necessarily account-authenticated;
   * `activateProfile` is what moves the active context to a profile.
   */
  public static async restore(): Promise<void> {
    if (TokenManager.mIsRestored) {
      return;
    }
    const pending: Promise<void> | null = TokenManager.mRestorePromise;
    if (pending !== null) {
      return pending;
    }

    const restore: Promise<void> = TokenManager.performRestore()
      .then((): void => {
        TokenManager.mIsRestored = true;
      })
      .finally((): void => {
        TokenManager.mRestorePromise = null;
      });
    TokenManager.mRestorePromise = restore;
    return restore;
  }

  private static async performRestore(): Promise<void> {
    TokenManager.mActiveProfileId = null;
    const bodyId: string = await DeviceModel.getBodyId();

    let accountToken: TStoredToken | null = await TokenStore.get(bodyId, null);

    if (accountToken === null) {
      // Upgrade path: a device last written by a build that stored the account
      // token under `accessTokenWithExpiry` still has a usable session.
      const legacy: TStoredToken | null =
        await TokenStore.readLegacyAccountToken();
      if (legacy !== null) {
        Log.i(TokenManager.TAG, "restore() migrating legacy account token");
        await TokenStore.set(bodyId, legacy);
        accountToken = legacy;
      }
    }
    await TokenStore.deleteLegacyAccountToken();

    if (accountToken !== null) {
      TokenManager.mEntries.set(ACCOUNT_CONTEXT_ID, {
        token: accountToken.token,
        domain: accountToken.domain,
        expirationMs: accountToken.expiration * 1000
      });
      Log.d(TokenManager.TAG, "restore() account context hydrated");
    }
  }

  /**
   * Returns a token guaranteed to be outside the expiry skew window,
   * refreshing first when it is not.
   *
   * @param profileId - Context to resolve against. Omit for the active one.
   * @throws UnauthenticatedError - No token for this context and none can be
   *         minted; the device has never signed in (or has signed out).
   * @throws UnauthorisedError - A token existed but could not be refreshed.
   */
  public static async getValidAccessToken(
    profileId?: string | null
  ): Promise<string> {
    const context: TAuthContext = await TokenManager.getAuthContext(profileId);
    return context.accessToken;
  }

  /**
   * The token plus the context it belongs to, read as one operation.
   *
   * Request builders need both — the `Authorization` header and the
   * `profileId` header must describe the same context, which reading them
   * separately cannot guarantee across a profile switch.
   */
  public static async getAuthContext(
    profileId?: string | null
  ): Promise<TAuthContext> {
    await TokenManager.restore();

    const context: string | null =
      profileId === undefined ? TokenManager.mActiveProfileId : profileId;
    const key: string = TokenManager.contextKey(context);

    const cached: TTokenEntry | undefined = TokenManager.mEntries.get(key);
    if (cached !== undefined && TokenManager.isFresh(cached.expirationMs)) {
      return TokenManager.toAuthContext(cached, context);
    }

    // Records never expire in storage, so what comes back may be stale or
    // long expired; the skew check decides whether it is fresh enough to hand
    // out, and a stale one still counts as a session to refresh.
    const bodyId: string = await DeviceModel.getBodyId();
    const stored: TStoredToken | null = await TokenStore.get(bodyId, context);
    if (stored !== null && TokenManager.isFresh(stored.expiration * 1000)) {
      const hydrated: TTokenEntry = {
        token: stored.token,
        domain: stored.domain,
        expirationMs: stored.expiration * 1000
      };
      TokenManager.mEntries.set(key, hydrated);
      return TokenManager.toAuthContext(hydrated, context);
    }

    await TokenManager.refreshContext(
      context,
      cached !== undefined || stored !== null
    );
    // refreshContext resolves only after adoptToken has written the entry.
    const minted: TTokenEntry | undefined = TokenManager.mEntries.get(key);
    if (minted === undefined) {
      throw new UnauthenticatedError();
    }
    return TokenManager.toAuthContext(minted, context);
  }

  private static toAuthContext(
    entry: TTokenEntry,
    profileId: string | null
  ): TAuthContext {
    return {
      accessToken: entry.token,
      profileId: profileId,
      domain: entry.domain,
      expiration: Math.floor(entry.expirationMs / 1000)
    };
  }

  /**
   * Whether a token for this context is already held and outside the skew
   * window — that is, whether {@link getValidAccessToken} would serve it
   * without a network call.
   *
   * Only meaningful after {@link restore}. Used to tell a restored session
   * apart from a refreshed one for reporting; never as a guard before asking
   * for a token, which handles every case itself.
   */
  public static hasFreshToken(profileId?: string | null): boolean {
    const context: string | null =
      profileId === undefined ? TokenManager.mActiveProfileId : profileId;
    const entry: TTokenEntry | undefined = TokenManager.mEntries.get(
      TokenManager.contextKey(context)
    );
    return entry !== undefined && TokenManager.isFresh(entry.expirationMs);
  }

  /**
   * Runs the refresh strategy for one context, joining an in-flight refresh
   * for the same context rather than issuing a second one.
   *
   * @param hadToken - Whether a token existed but was stale, which is the
   *                   difference between "not signed in" and "refresh failed".
   */
  private static async refreshContext(
    profileId: string | null,
    hadToken: boolean
  ): Promise<string> {
    const key: string = TokenManager.contextKey(profileId);

    const pending: Promise<string> | undefined =
      TokenManager.mInFlight.get(key);
    if (pending !== undefined) {
      Log.d(TokenManager.TAG, "refreshContext() joining in-flight " + key);
      return pending;
    }

    const strategy: ITokenRefreshStrategy | null = TokenManager.mStrategy;
    if (strategy === null) {
      Log.e(
        TokenManager.TAG,
        "refreshContext() no refresh strategy registered"
      );
      throw hadToken ? new UnauthorisedError() : new UnauthenticatedError();
    }

    Log.d(TokenManager.TAG, "refreshContext() refreshing " + key);
    const refresh: Promise<string> = strategy
      .refresh(profileId)
      .then(async (domainToken: DomainToken): Promise<string> => {
        await TokenManager.adoptToken(domainToken, profileId);
        return domainToken.token;
      })
      .catch((error: unknown): never => {
        Log.e(TokenManager.TAG, "refreshContext() failed for " + key, error);
        // A context that had a token and lost it is a *failed renewal*: the
        // strategy's own error says which path gave out, and callers surface
        // that. A context that never had one is simply not signed in, which
        // is a distinct outcome (and the one boot rehydration reports as
        // "no session" rather than as an error).
        if (!hadToken) {
          throw new UnauthenticatedError();
        }
        throw error;
      })
      .finally((): void => {
        TokenManager.mInFlight.delete(key);
      });

    TokenManager.mInFlight.set(key, refresh);
    return refresh;
  }

  /**
   * Takes ownership of a freshly minted token: memory and IndexedDB together.
   *
   * Every flow that obtains a token — interactive sign-in, profile activation,
   * a strategy refresh — ends here, so persistence can never be forgotten at a
   * call site.
   */
  public static async adoptToken(
    domainToken: DomainToken,
    profileId: string | null
  ): Promise<void> {
    const key: string = TokenManager.contextKey(profileId);
    TokenManager.mEntries.set(key, {
      token: domainToken.token,
      domain: domainToken.domain,
      expirationMs: domainToken.expiration * 1000
    });
    bumpTokenGeneration();

    try {
      const bodyId: string = await DeviceModel.getBodyId();
      await TokenStore.set(bodyId, {
        token: domainToken.token,
        domain: domainToken.domain,
        expiration: domainToken.expiration,
        profileId: profileId
      });
    } catch (error: unknown) {
      // The in-memory token is still good for this session; losing the write
      // costs a refresh on next boot, which is not worth failing sign-in over.
      Log.e(TokenManager.TAG, "adoptToken() persist failed for " + key, error);
    }
  }

  /**
   * Re-persists the session under the device's current bodyId.
   *
   * Persisted tokens are keyed by bodyId, and a device signing in for the
   * first time only learns its bodyId from `provisionDevices` — which runs
   * *after* the domain token has been adopted. That first session is therefore
   * written under the empty bodyId the device had at the time, while the next
   * boot restores under the provisioned one, finds nothing, and sends the user
   * back to the sign-in screen. Moving the records closes that window.
   *
   * A failure here costs a re-authentication on the next boot, never the
   * sign-in in progress, so it is logged rather than thrown.
   *
   * @param previousBodyId - The bodyId in force when the tokens were written.
   */
  public static async rebindToCurrentBody(
    previousBodyId: string
  ): Promise<void> {
    try {
      const bodyId: string = await DeviceModel.getBodyId();
      if (bodyId === previousBodyId) {
        return;
      }
      await TokenStore.rekey(previousBodyId, bodyId);
      Log.i(
        TokenManager.TAG,
        "rebindToCurrentBody() tokens re-keyed to the provisioned body"
      );
    } catch (error: unknown) {
      Log.e(TokenManager.TAG, "rebindToCurrentBody()", error);
    }
  }

  /**
   * Makes `profileId` the context that argument-less accessors resolve
   * against, and mirrors it onto TivoSettings so request builders that read
   * the profile ID from there stay in step.
   */
  public static async setActiveContext(
    profileId: string | null
  ): Promise<void> {
    await TokenManager.restore();
    TokenManager.mActiveProfileId = profileId;
    DeviceModel.getTiVoSettings().setProfileId(
      profileId === null ? "" : profileId
    );
    Log.d(
      TokenManager.TAG,
      "setActiveContext() " + TokenManager.contextKey(profileId)
    );
  }

  /** The context argument-less accessors currently resolve against. */
  public static getActiveProfileId(): string | null {
    return TokenManager.mActiveProfileId;
  }

  /**
   * Marks a context's token as expired so the next read refreshes it.
   *
   * The 401 path: the service rejected a token this client believed valid
   * (clock skew, server-side revocation), so the local freshness check cannot
   * be trusted and the token must be re-minted.
   *
   * The token is expired **in place**, in memory and in IndexedDB, rather than
   * deleted. A rejected token is still a session: if the refresh that follows
   * fails, the next attempt — or the next boot — must refresh again, not treat
   * the device as never signed in. Only {@link clearContext} and {@link clear}
   * remove a record.
   */
  public static async invalidate(profileId?: string | null): Promise<void> {
    const context: string | null =
      profileId === undefined ? TokenManager.mActiveProfileId : profileId;
    const key: string = TokenManager.contextKey(context);
    const cached: TTokenEntry | undefined = TokenManager.mEntries.get(key);
    if (cached !== undefined) {
      TokenManager.mEntries.set(key, {
        token: cached.token,
        domain: cached.domain,
        expirationMs: 0
      });
    }
    Log.d(TokenManager.TAG, "invalidate() " + key);
    try {
      const bodyId: string = await DeviceModel.getBodyId();
      const stored: TStoredToken | null =
        cached !== undefined
          ? {
              token: cached.token,
              domain: cached.domain,
              expiration: 0,
              profileId: context
            }
          : await TokenStore.get(bodyId, context);
      if (stored !== null) {
        await TokenStore.set(bodyId, {
          token: stored.token,
          domain: stored.domain,
          expiration: 0,
          profileId: context
        });
      }
    } catch (error: unknown) {
      Log.e(TokenManager.TAG, "invalidate() store update failed", error);
    }
  }

  /**
   * Forgets one context entirely — memory and IndexedDB. Used when a profile
   * is deleted; the caller is expected to follow with
   * `setActiveContext(null)` to fall back to the account token.
   */
  public static async clearContext(profileId: string | null): Promise<void> {
    const key: string = TokenManager.contextKey(profileId);
    TokenManager.mEntries.delete(key);
    Log.d(TokenManager.TAG, "clearContext() " + key);
    try {
      const bodyId: string = await DeviceModel.getBodyId();
      await TokenStore.delete(bodyId, profileId);
    } catch (error: unknown) {
      Log.e(TokenManager.TAG, "clearContext() store delete failed", error);
    }
  }

  /**
   * Wipes every context, in memory and on disk, and unsets the active one.
   * Sign-out.
   */
  public static async clear(): Promise<void> {
    TokenManager.mEntries.clear();
    TokenManager.mInFlight.clear();
    TokenManager.mActiveProfileId = null;
    TokenManager.mIsRestored = false;
    TokenManager.mRestorePromise = null;
    try {
      await TokenStore.deleteAll();
    } catch (error: unknown) {
      Log.e(TokenManager.TAG, "clear() store wipe failed", error);
    }
  }

  /**
   * Whether a token for this context is held at all, fresh or not.
   *
   * The difference from {@link hasFreshToken} is the one that decides whether
   * a refresh is worth attempting: a stale token means a session that needs
   * renewing, no token at all means a device that has nothing to renew. Asking
   * {@link getValidAccessToken} instead would run the whole re-authentication
   * walk against such a device — several service calls that can only fail.
   *
   * Only meaningful after {@link restore}, and only for callers deciding
   * whether a session exists; never as a guard before
   * {@link getValidAccessToken}, which handles the empty case itself.
   *
   * @param profileId - Context to check. Omit for the active one.
   */
  public static hasToken(profileId?: string | null): boolean {
    const context: string | null =
      profileId === undefined ? TokenManager.mActiveProfileId : profileId;
    return TokenManager.mEntries.has(TokenManager.contextKey(context));
  }

  /**
   * Test-only: drops in-memory state without touching storage, so a suite can
   * start from a known baseline.
   */
  public static __resetForTest(): void {
    TokenManager.mEntries.clear();
    TokenManager.mInFlight.clear();
    TokenManager.mStrategy = null;
    TokenManager.mActiveProfileId = null;
    TokenManager.mIsRestored = false;
    TokenManager.mRestorePromise = null;
  }
}
